/**
 * Persona traits API binding.
 *
 * Cyrano FastAPI expose les traits sous `/api/creators/{cyrano_uuid}/persona-traits`
 * (router `app/api/routes/persona_traits.py`, auth WorkOS JWT, ACL via
 * `creators.workos_org_id`). Le cockpit identifie ses creators via Convex
 * `_id` + slug. Le pont est le slug (= `Creator.name` cote Postgres =
 * `creators.slug` cote Convex). On expose donc :
 *
 *   - `useCyranoCreatorIdBySlug(slug)` : resout slug Convex -> UUID Cyrano,
 *     via `GET /api/creators` une fois par session (staleTime 10 min).
 *
 *   - Service CRUD `personaTraitsService` qui copie 1:1 le contrat
 *     legacy admin (CRUD + import async + import-status + reindex).
 *
 *   - Hooks React Query qui prennent le slug Convex et resolvent l'UUID
 *     en interne avant d'enqueue la query.
 *
 * Auth : cookie de session pose par `/api/auth/login` (WorkOS bridge cote
 * FastAPI). Le middleware backend lit le JWT et applique l'ACL org. Aucun
 * token cote composant. En prod, nginx proxifie `/api/*` vers `cyrano.dozka.net`
 * (cf `deploy/nginx.conf`).
 */

import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api as cyranoApi } from "@/core/api/client";

// ─── Types (alignes avec backend `app/api/admin/schemas/persona_traits.py`) ──

export interface PersonaTrait {
  id: string;
  creator_id: string;
  category: string;
  label: string;
  value: string;
  search_text: string;
  display_order: number;
  has_embedding: boolean;
  created_at: string;
  updated_at: string;
}

export interface PersonaTraitCreate {
  category: string;
  label: string;
  value: string;
  display_order?: number;
}

export interface PersonaTraitUpdate {
  category?: string;
  label?: string;
  value?: string;
  display_order?: number;
}

export interface PersonaTraitImportItem {
  category: string;
  label: string;
  value: string;
  display_order?: number;
}

export interface PersonaTraitImportResponse {
  imported: number;
  status: string;
}

export type PersonaImportStatus = "idle" | "running" | "done" | "failed";

export interface PersonaTraitImportStatusResponse {
  status: PersonaImportStatus;
  indexed: number;
  total: number;
  failed: number;
}

// ─── Service CRUD ────────────────────────────────────────────────────────────

function basePath(creatorId: string): string {
  return `/api/creators/${creatorId}/persona-traits`;
}

export const personaTraitsService = {
  list: (creatorId: string) =>
    cyranoApi.get<PersonaTrait[]>(basePath(creatorId)),

  create: (creatorId: string, data: PersonaTraitCreate) =>
    cyranoApi.post<PersonaTrait>(basePath(creatorId), data),

  update: (
    creatorId: string,
    traitId: string,
    data: PersonaTraitUpdate,
    skipReindex = false,
  ) =>
    cyranoApi.patch<PersonaTrait>(
      `${basePath(creatorId)}/${traitId}`,
      data,
      { params: skipReindex ? { skip_reindex: true } : undefined },
    ),

  delete: (creatorId: string, traitId: string) =>
    cyranoApi.delete<void>(`${basePath(creatorId)}/${traitId}`),

  importTraits: (creatorId: string, traits: PersonaTraitImportItem[]) =>
    cyranoApi.post<PersonaTraitImportResponse>(
      `${basePath(creatorId)}/import`,
      { traits },
    ),

  getImportStatus: (creatorId: string) =>
    cyranoApi.get<PersonaTraitImportStatusResponse>(
      `${basePath(creatorId)}/import-status`,
    ),

  reindex: (creatorId: string, force = false) =>
    cyranoApi.post<{ indexed: number; creator_id: string }>(
      `${basePath(creatorId)}/reindex`,
      undefined,
      { params: { force } },
    ),
};

// ─── slug -> Cyrano UUID resolver ────────────────────────────────────────────

interface ApiCreatorRow {
  id: string;
  slug: string;
}

interface ApiCreatorsListResponse {
  creators: ApiCreatorRow[];
}

/**
 * Fetch `/api/creators` puis projete en `slug -> uuid`.
 *
 * ⚠️ Auto-rafraichissement (2026-06-27) : une creatrice fraichement creee
 * cote cockpit (Convex) n'apparait dans `/api/creators` (Cyrano/Neon) qu'apres
 * la synchro asynchrone Convex -> Cyrano (quelques secondes). Avant ce fix, la
 * liste etait figee 10 min sans refetch (`refetchOnWindowFocus: false`) : tant
 * que le slug n'etait pas resolu, `cyranoId` restait `undefined` et TOUS les
 * boutons des sections (Commandes, Publications, Flows, ...) restaient grises
 * jusqu'a un hard refresh manuel. Desormais :
 *   - `staleTime` court + refetch au focus ;
 *   - SURTOUT, quand on attend un slug precis (`awaitSlug`) pas encore resolu,
 *     on POLL la liste toutes les 2.5s jusqu'a ce qu'il apparaisse, puis on
 *     s'arrete. Le degrisage devient automatique, sans action utilisateur.
 *
 * `awaitSlug` absent (ex: picker listant toutes les creatrices) => aucun poll.
 */
export function useCyranoCreatorsMap(awaitSlug?: string | null): {
  bySlug: Map<string, string>;
  isLoading: boolean;
} {
  const { data, isLoading } = useQuery({
    queryKey: ["cyrano-creators", "slug-to-id"],
    queryFn: async () =>
      // `include_sites=true` : c'est un RÉSOLVEUR slug → UUID, pas un écran.
      // Les périmètres de site sont exclus par défaut de `/api/creators`
      // (chantier du 27-08-2026) et l'app « Twitter — Sites » ne pourrait plus
      // résoudre `cumgram` — donc plus d'Analytics ni de Vault pour un site.
      // Tous les appelants ne font que `bySlug.get(slug)` ; élargir la table
      // n'ajoute personne nulle part.
      cyranoApi.get<ApiCreatorsListResponse>("/api/creators?include_sites=true"),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    refetchInterval: (query) => {
      if (!awaitSlug) return false;
      const rows = (query.state.data as ApiCreatorsListResponse | undefined)
        ?.creators;
      const resolved =
        Array.isArray(rows) && rows.some((c) => c?.slug === awaitSlug);
      // Pas encore sync cote Cyrano -> on continue de poller ; sinon stop.
      return resolved ? false : 2500;
    },
  });
  const bySlug = useMemo(() => {
    const map = new Map<string, string>();
    const rows = (data as ApiCreatorsListResponse | undefined)?.creators;
    if (Array.isArray(rows)) {
      for (const c of rows) {
        if (c?.slug && c?.id) map.set(c.slug, c.id);
      }
    }
    return map;
  }, [data]);
  return { bySlug, isLoading };
}

/**
 * Resoud un slug Convex en UUID Cyrano. Retourne `undefined` tant que le
 * mapping n'est pas charge, ou si le slug n'existe pas cote Cyrano (creator
 * present dans Convex mais pas encore sync sur Neon). Tant que le slug demande
 * n'est pas resolu, la liste est pollee automatiquement (cf `useCyranoCreatorsMap`).
 */
export function useCyranoCreatorIdBySlug(slug: string | null | undefined): {
  cyranoId: string | undefined;
  isResolving: boolean;
} {
  const { bySlug, isLoading } = useCyranoCreatorsMap(slug);
  return {
    cyranoId: slug ? bySlug.get(slug) : undefined,
    isResolving: isLoading,
  };
}

// ─── React Query hooks ──────────────────────────────────────────────────────

/**
 * Liste des traits pour un creator (resolution slug -> UUID en interne).
 * Pas de fetch tant que le mapping n'est pas charge.
 */
export function usePersonaTraitsList(slug: string | null | undefined) {
  const { cyranoId, isResolving } = useCyranoCreatorIdBySlug(slug);
  const query = useQuery({
    queryKey: ["persona-traits", cyranoId],
    queryFn: () => personaTraitsService.list(cyranoId!),
    enabled: Boolean(cyranoId),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
  return {
    cyranoId,
    traits: query.data ?? [],
    isLoading: isResolving || query.isLoading,
    isError: query.isError,
    error: query.error as Error | null,
  };
}

export function useCreatePersonaTrait(cyranoId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: PersonaTraitCreate) => {
      if (!cyranoId) throw new Error("creator not resolved");
      return personaTraitsService.create(cyranoId, data);
    },
    onSuccess: () => {
      if (cyranoId) qc.invalidateQueries({ queryKey: ["persona-traits", cyranoId] });
    },
  });
}

export function useUpdatePersonaTrait(cyranoId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      traitId: string;
      data: PersonaTraitUpdate;
      skipReindex?: boolean;
    }) => {
      if (!cyranoId) throw new Error("creator not resolved");
      return personaTraitsService.update(
        cyranoId,
        vars.traitId,
        vars.data,
        vars.skipReindex ?? false,
      );
    },
    onSuccess: () => {
      if (cyranoId) qc.invalidateQueries({ queryKey: ["persona-traits", cyranoId] });
    },
  });
}

export function useDeletePersonaTrait(cyranoId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (traitId: string) => {
      if (!cyranoId) throw new Error("creator not resolved");
      return personaTraitsService.delete(cyranoId, traitId);
    },
    onSuccess: () => {
      if (cyranoId) qc.invalidateQueries({ queryKey: ["persona-traits", cyranoId] });
    },
  });
}

export function useImportPersonaTraits(cyranoId: string | undefined) {
  return useMutation({
    mutationFn: (items: PersonaTraitImportItem[]) => {
      if (!cyranoId) throw new Error("creator not resolved");
      return personaTraitsService.importTraits(cyranoId, items);
    },
  });
}
